const DAYS_PER_WEEK = 7;
const DEFAULT_EVENT_HOUR = 18;

/** Midnight (local time) of the given day. */
export const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const startOfMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), 1);

export const addMonths = (date: Date, months: number): Date =>
  new Date(date.getFullYear(), date.getMonth() + months, 1);

export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** Local day key ("2026-11-05"), to group events by the day they start. */
export const toDayKey = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

/** Weeks start on Monday, as in Switzerland and France. */
const daysSinceMonday = (date: Date) => (date.getDay() + 6) % DAYS_PER_WEEK;

/**
 * Days displayed by a month view: whole weeks, Monday to Sunday, from the week
 * of the 1st to the week of the last day of the month.
 */
export const getMonthGrid = (month: Date): Date[][] => {
  const first = startOfMonth(month);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
  const gridStart = addDays(first, -daysSinceMonday(first));
  const gridEnd = addDays(last, DAYS_PER_WEEK - 1 - daysSinceMonday(last));
  const weeks: Date[][] = [];
  for (let day = gridStart; day <= gridEnd; day = addDays(day, 1)) {
    if (daysSinceMonday(day) === 0) weeks.push([]);
    weeks[weeks.length - 1].push(day);
  }
  return weeks;
};

/** Time range covered by a month view: [first displayed day, day after the last one). */
export const getMonthGridRange = (month: Date): { start: Date; end: Date } => {
  const weeks = getMonthGrid(month);
  const lastWeek = weeks[weeks.length - 1];
  return {
    start: weeks[0][0],
    end: addDays(lastWeek[lastWeek.length - 1], 1),
  };
};

/** Default start of an event created from a calendar day: that day at 18:00. */
export const getDefaultEventStart = (day: Date): string => {
  const start = startOfDay(day);
  start.setHours(DEFAULT_EVENT_HOUR);
  return start.toISOString();
};
